import { useGetCompany } from "@/api/companies";
import dayjs, { Dayjs } from "dayjs";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

export enum Shortcut {
  ALL = "all",
  TODAY = "today",
  TOMORROW = "tomorrow",
  YESTERDAY = "yesterday",
  LAST_7_DAYS = "last7days",
  LAST_30_DAYS = "last30days",
  NEXT_7_DAYS = "next7days",
  NEXT_30_DAYS = "next30days",
  CUSTOM = "custom",
}

export enum RouteName {
  SPACES_BOOKINGS = "spaces-bookings",
  EMPLOYEES_TIMELINE = "employees-timeline",
  PREREGISTRATION = "visitors-preregistration",
  DELIVERIES = "deliveries",
  VISITORS_TIMELINE = "visitors-timeline",
  SPACE_CATEGORY_SETTINGS = "spaces-categories-settings",
}

export type DateRangeFilter = {
  dateRange: [Date, Date];
  shortcut: Shortcut;
};

export type DateShortcut = {
  shortcut: Shortcut;
  text: string;
  value: () => Dayjs[];
};

export const useDateShortcuts = () => {
  const { t } = useI18n();
  const { data: company } = useGetCompany();
  const route = useRoute();

  const shortcuts = computed<Shortcut[]>(() => {
    const routeName = route.name;
    const DEFAULT_LIST = [Shortcut.ALL, Shortcut.TODAY];

    switch (routeName) {
      case RouteName.SPACES_BOOKINGS:
        return [
          ...DEFAULT_LIST,
          Shortcut.TOMORROW,
          Shortcut.NEXT_7_DAYS,
          Shortcut.LAST_7_DAYS,
          Shortcut.NEXT_30_DAYS,
          Shortcut.LAST_30_DAYS,
        ];

      case RouteName.PREREGISTRATION:
        return [
          ...DEFAULT_LIST,
          Shortcut.TOMORROW,
          Shortcut.NEXT_7_DAYS,
          Shortcut.NEXT_30_DAYS,
        ];

      case RouteName.DELIVERIES:
      case RouteName.VISITORS_TIMELINE:
      case RouteName.EMPLOYEES_TIMELINE:
      default:
        return [
          ...DEFAULT_LIST,
          Shortcut.YESTERDAY,
          Shortcut.LAST_7_DAYS,
          Shortcut.LAST_30_DAYS,
        ];
    }
  });

  const defaultShortcut = computed<Shortcut>(() => {
    const routeName = route?.name ?? "";

    switch (routeName) {
      case RouteName.PREREGISTRATION:
        return Shortcut.NEXT_7_DAYS;

      case RouteName.SPACES_BOOKINGS:
        return Shortcut.TODAY;

      case RouteName.VISITORS_TIMELINE:
      case RouteName.DELIVERIES:
      case RouteName.EMPLOYEES_TIMELINE:
      default:
        return Shortcut.LAST_7_DAYS;
    }
  });

  const getInitShortcut = computed(() => {
    const shortcut = (route.query?.date as Shortcut) ?? defaultShortcut.value;

    return shortcuts.value.includes(shortcut)
      ? shortcut
      : defaultShortcut.value;
  });

  const selectedShortcut = ref<Shortcut>(getInitShortcut.value);

  const clearSelectedShortcut = () => {
    selectedShortcut.value = Shortcut.CUSTOM;
  };

  const getShortcuts = (list: Shortcut[] = shortcuts.value) => {
    const results: DateShortcut[] = [];

    list.forEach((shortcut) => {
      switch (shortcut) {
        case Shortcut.ALL:
          results.push(ALL.value);
          break;
        case Shortcut.LAST_30_DAYS:
          results.push(LAST_30_DAYS.value);
          break;
        case Shortcut.LAST_7_DAYS:
          results.push(LAST_7_DAYS.value);
          break;
        case Shortcut.NEXT_30_DAYS:
          results.push(NEXT_30_DAYS.value);
          break;
        case Shortcut.NEXT_7_DAYS:
          results.push(NEXT_7_DAYS.value);
          break;
        case Shortcut.TODAY:
          results.push(TODAY.value);
          break;
        case Shortcut.TOMORROW:
          results.push(TOMORROW.value);
          break;
        case Shortcut.YESTERDAY:
          results.push(YESTERDAY.value);
          break;
        default:
      }
    });

    return results;
  };

  const LAST_30_DAYS = computed(() => {
    return {
      shortcut: Shortcut.LAST_30_DAYS,
      text: t("datePresets.last30days"),
      value: () => {
        const start = dayjs().subtract(1, "month").startOf("day");
        const end = dayjs().endOf("day");
        selectedShortcut.value = Shortcut.LAST_30_DAYS;
        return [start, end];
      },
    };
  });

  const LAST_7_DAYS = computed(() => {
    return {
      shortcut: Shortcut.LAST_7_DAYS,
      text: t("datePresets.last7days"),
      value: () => {
        const start = dayjs().subtract(7, "day").startOf("day");
        const end = dayjs().endOf("day");
        selectedShortcut.value = Shortcut.LAST_7_DAYS;
        return [start, end];
      },
    };
  });

  const NEXT_30_DAYS = computed(() => {
    return {
      shortcut: Shortcut.NEXT_30_DAYS,
      text: t("datePresets.next30days"),
      value: () => {
        const start = dayjs().startOf("day");
        const end = dayjs().add(30, "day").endOf("day");
        selectedShortcut.value = Shortcut.NEXT_30_DAYS;
        return [start, end];
      },
    };
  });

  const NEXT_7_DAYS = computed(() => {
    return {
      shortcut: Shortcut.NEXT_7_DAYS,
      text: t("datePresets.next7days"),
      value: () => {
        const start = dayjs().startOf("day");
        const end = dayjs().add(7, "day").endOf("day");
        selectedShortcut.value = Shortcut.NEXT_7_DAYS;
        return [start, end];
      },
    };
  });

  const TODAY = computed(() => {
    return {
      shortcut: Shortcut.TODAY,
      text: t("datePresets.today"),
      value: () => {
        const start = dayjs().startOf("day");
        const end = dayjs().endOf("day");
        selectedShortcut.value = Shortcut.TODAY;
        return [start, end];
      },
    };
  });

  const TOMORROW = computed(() => {
    return {
      shortcut: Shortcut.TOMORROW,
      text: t("datePresets.tomorrow"),
      value: () => {
        const start = dayjs().add(1, "day").startOf("day");
        const end = dayjs().add(1, "day").endOf("day");
        selectedShortcut.value = Shortcut.TOMORROW;
        return [start, end];
      },
    };
  });

  const YESTERDAY = computed(() => {
    return {
      shortcut: Shortcut.YESTERDAY,
      text: t("datePresets.yesterday"),
      value: () => {
        const start = dayjs().subtract(1, "day").startOf("day");
        const end = dayjs().subtract(1, "day").endOf("day");
        selectedShortcut.value = Shortcut.YESTERDAY;
        return [start, end];
      },
    };
  });

  const ALL = computed(() => {
    return {
      shortcut: Shortcut.ALL,
      text: t("datePresets.all"),
      value: () => {
        let start = dayjs(company.value?.subscription.joinedAt).startOf("day");
        let end = dayjs().endOf("day");

        if (route.name === RouteName.PREREGISTRATION) {
          start = dayjs().startOf("day");
          end = dayjs().add(2, "years").endOf("day");
        }

        if (
          route.name === RouteName.SPACES_BOOKINGS ||
          route.name === RouteName.SPACE_CATEGORY_SETTINGS
        ) {
          end = dayjs().add(2, "years").endOf("day");
        }

        selectedShortcut.value = Shortcut.ALL;
        return [start, end];
      },
    };
  });

  const getShortcutDateRange = (shortcut: Shortcut): [Date, Date] => {
    const dateShortcut = getShortcuts([shortcut])[0].value() as Dayjs[];
    return [dateShortcut[0].toDate(), dateShortcut[1].toDate()];
  };

  return {
    selectedShortcut,
    getShortcuts,
    getShortcutDateRange,
    clearSelectedShortcut,
    getInitShortcut,
    defaultShortcut,
  };
};
